import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Car } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import DisclosureBanner from "../components/DisclosureBanner";
import { StepVehicleAccess, StepVehicle, StepPickup } from "./avp/StepsVehicle";
import { StepPhysical } from "./avp/StepsFinal";

const filled = (obj, keys) => keys.every((k) => obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "");

export default function AvpDetails() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ vehicle_access: {}, vehicle: {}, pickup: {}, physical: {}, documents: {} });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/avp-details/${token}`).then(({ data }) => { setInfo(data); setForm((f) => ({ ...f, vehicle_access: data.vehicle_access || {} })); setState(data.submitted_at ? "done" : "form"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const setSec = (sec) => (k, v) => setForm((f) => ({ ...f, [sec]: { ...f[sec], [k]: v } }));
  const hasVehicle = form.vehicle_access.has_vehicle === "yes";
  const isPickup = hasVehicle && form.vehicle.vehicle_type === "pickup_truck";

  const steps = useMemo(() => {
    const s = [{ key: "vehicle_access", title: "Vehicle Access", valid: () => !!form.vehicle_access.has_vehicle && (form.vehicle_access.has_vehicle === "no" || (form.vehicle_access.ownership && form.vehicle_access.vehicle_available_all_times)) }];
    if (hasVehicle) {
      s.push({ key: "vehicle", title: "Vehicle", valid: () => filled(form.vehicle, ["vehicle_type", "year", "make", "model", "color", "plate", "plate_state", "mileage", "cargo_space", "enclosed_lockable", "safe_reliable", "equipment_ok"]) });
      if (isPickup) s.push({ key: "pickup", title: "Pickup Bed", valid: () => filled(form.pickup, ["bed_covered", "weather_resistant", "lockable"]) });
    }
    s.push({ key: "physical", title: "Requirements", valid: () => filled(form.physical, ["lift_50", "mobility", "smartphone", "use_tech", "service_windows"]) });
    return s;
  }, [form, hasVehicle, isPickup]);
  const idx = Math.min(step, steps.length - 1);
  const cur = steps[idx];

  const submit = async () => {
    setBusy(true);
    try { await api.post(`/avp-details/${token}`, { vehicle_access: form.vehicle_access, vehicle: form.vehicle, pickup: form.pickup, physical: form.physical }); setState("done"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="avp-details-page">
      <DisclosureBanner />
      <section className="py-10 md:py-14 border-b border-slate-900 gradient-radial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Car className="w-4 h-4" /> AVP · Step 2 of the process</div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">Your vehicle details</h1>
          {info?.first_name && state === "form" && <p className="mt-4 text-slate-300 text-sm sm:text-base">Hi {info.first_name} — management would like to move forward with your {info.shift_label || "AVP"} application. Tell us about the vehicle you plan to use. Documents will be requested separately through a secure link.</p>}
        </div>
      </section>
      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {state === "loading" && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>}
          {state === "invalid" && <div data-testid="avp-details-invalid" className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-300">This link is not valid or is no longer active. Please contact us at 667-434-4392.</div>}
          {state === "done" && (
            <div data-testid="avp-details-done" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
              <h2 className="mt-4 text-2xl font-extrabold uppercase display text-white">Vehicle details received</h2>
              <p className="mt-3 text-slate-300">Thank you. Our team will review your vehicle information and, if everything looks good, e-mail you a secure link to upload your registration, insurance, and vehicle photos.</p>
              <Link to="/"><Button variant="outline" className="mt-6 border-slate-700 text-slate-200">Back to home</Button></Link>
            </div>
          )}
          {state === "form" && (
            <>
              <div data-testid="avp-details-step-indicator" className="text-xs text-slate-400">Step {idx + 1} of {steps.length} · {cur.title}</div>
              <div data-testid={`avp-details-step-${cur.key}`} className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8">
                {cur.key === "vehicle_access" && <StepVehicleAccess d={form.vehicle_access} set={setSec("vehicle_access")} docs={form.documents} />}
                {cur.key === "vehicle" && <StepVehicle d={form.vehicle} set={setSec("vehicle")} />}
                {cur.key === "pickup" && <StepPickup d={form.pickup} set={setSec("pickup")} docs={form.documents} />}
                {cur.key === "physical" && <StepPhysical d={form.physical} set={setSec("physical")} />}
              </div>
              <div className="mt-6 flex items-center justify-between gap-3">
                <Button data-testid="avp-details-back" variant="outline" className="border-slate-700 text-slate-200" disabled={idx === 0} onClick={() => setStep(idx - 1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                {idx < steps.length - 1
                  ? <Button data-testid="avp-details-next" disabled={!cur.valid()} onClick={() => setStep(idx + 1)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
                  : <Button data-testid="avp-details-submit" disabled={!cur.valid() || busy} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Submit vehicle details</Button>}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
